import React, {
  useCallback, useEffect, useState, useMemo, Suspense,
} from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import { Theme, styled } from '@mui/material/styles';
import { withAuthGuard } from 'hooks/with-auth-guard';
import {
  Box, IconButton, LinearProgress, Link, SvgIcon, ThemeOptions, Tooltip, useMediaQuery,
} from '@mui/material';
import { LogoNoLogo } from 'components/logos/logo_no_logo';
import { PageMetadataProvider } from 'contexts/page-metadata-context';
import { useAuthContext } from 'contexts/auth-context';
import UserCircleIcon from '@heroicons/react/24/solid/UserCircleIcon';
import Bars4Icon from '@heroicons/react/24/solid/Bars4Icon';
import ChatBubbleLeftRightIcon from '@heroicons/react/24/solid/ChatBubbleLeftRightIcon';
import SparklesIcon from '@heroicons/react/24/solid/SparklesIcon';
import Chatbot from 'components/chatbot/chatbot';
import { PageAssistant } from 'components/page-assistant/page-assistant';
import { setPageCaptureEnabled } from 'utils/page-data-capture';
import { PermissionsTypes } from 'model/role-permissions';
import { userHasPermissionOnResource } from 'utils/user-permissions';
import { ImpersonationBanner } from 'components/auth/impersonation-banner';
import { MaintenanceBanner } from 'components/maintenance/maintenance-banner';
import { SideNav } from './side-nav';
import styles from './layout.module.scss';

const SIDE_NAV_WIDTH = 280;

const LayoutRoot = styled('div')(({ theme }: { theme: Theme }) => ({
  [theme.breakpoints.up('lg')]: {
    paddingLeft: SIDE_NAV_WIDTH,
  },
}));

export const Layout = withAuthGuard(() => {
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  const [openNav, setOpenNav] = useState(false);
  const [openChatbot, setOpenChatbot] = useState(false);
  const [openAssistant, setOpenAssistant] = useState(false);
  // Local LM Studio assistant — personal tool, default Deny.
  const hasLocalAssistant = userHasPermissionOnResource(PermissionsTypes.Read, 'local_lm_studio_user', user?.ownPermissions, false);
  // Page-data capture runs only for users who have the assistant.
  useEffect(() => { setPageCaptureEnabled(hasLocalAssistant); }, [hasLocalAssistant]);
  const lgUp = useMediaQuery((theme: ThemeOptions) => ((theme.breakpoints && theme.breakpoints.up) ? theme.breakpoints.up('lg') : ''));

  const toggleDrawer = () => setOpenNav((prev) => !prev);
  const toggleChatbot = () => {
    setOpenChatbot((prev) => !prev);
  };
  const handlePathnameChange = useCallback(
    () => {
      if (openNav) {
        setOpenNav(false);
      }
    },
    [openNav],
  );

  useEffect(
    () => {
      handlePathnameChange();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname],
  );

  const LogoComponent = useMemo(() => <LogoNoLogo />, []);

  const MyProfileButton = useMemo(() => {
    if (user?.person_id && user?.person_id !== '') {
      return (
        <Box className={styles.my_profile_link_container}>
          <Tooltip title="Go to your profile">
            <Link component={RouterLink} to={`/people/details/?personId=${user?.person_id}`}>
              <SvgIcon fontSize="large" className={styles.my_profile_icon}>
                <UserCircleIcon />
              </SvgIcon>
            </Link>
          </Tooltip>
        </Box>
      );
    }
    return null;
  }, [user]);

  const MyChatbotButton = useMemo(() => {
    // Hide the component if the user does not have ReadWrite permission
    if (!userHasPermissionOnResource(PermissionsTypes.ReadWrite, 'chatbot', user?.ownPermissions, false)) {
      return null;
    }
    // If the user has permission, show the chatbot button
    return (
      <Box>
        <Tooltip title="Open chatbot">
          <IconButton onClick={toggleChatbot}>
            <SvgIcon fontSize="large">
              <ChatBubbleLeftRightIcon />
            </SvgIcon>
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, [user?.ownPermissions]);

  const MyAssistantButton = hasLocalAssistant ? (
    <Box>
      <Tooltip title="Ask about this page (LM Studio on this computer)">
        <IconButton onClick={() => setOpenAssistant((prev) => !prev)} sx={{ color: (theme) => theme.palette.grey[100], mr: 1 }}>
          <SvgIcon fontSize="large">
            <SparklesIcon />
          </SvgIcon>
        </IconButton>
      </Tooltip>
    </Box>
  ) : null;

  return (
    <html lang="en">
      <PageMetadataProvider>
        <SideNav onClose={() => setOpenNav(false)} open={openNav} permanent={lgUp} />
        <Chatbot onClose={() => setOpenChatbot(false)} open={openChatbot} />
        {hasLocalAssistant && <PageAssistant open={openAssistant} onClose={() => setOpenAssistant(false)} />}
        <LayoutRoot className={styles.layout_root}>
          <div className={styles.layout_container}>
            <ImpersonationBanner />
            <MaintenanceBanner />
            <Box
              className={styles.top_logo_row}
              component="main"
              height={130}
            >
              {!lgUp && (
                <IconButton onClick={toggleDrawer} sx={{ color: 'white' }}>
                  <SvgIcon fontSize="small">
                    <Bars4Icon />
                  </SvgIcon>
                </IconButton>
              )}
              {LogoComponent}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {MyAssistantButton}
                  {MyProfileButton}
                </Box>
                {MyChatbotButton}
              </Box>
            </Box>

            {/* data-page-content: the assistant's "visible text" source (excludes nav / header). */}
            <div data-page-content>
              <Suspense fallback={<LinearProgress />}>
                <Outlet />
              </Suspense>
            </div>
          </div>
        </LayoutRoot>
      </PageMetadataProvider>
    </html>
  );
});
